import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { AppRoutePaths } from '../../../../routing/route_paths.ts'
// import { BackToChecklist } from '@/pages/program_applications/vendor_application/components/WizardStepTitle.tsx'

export type StepWithoutChildren = {
  id: 0 | 1 | 2 | 4 | 5
  name: string
  title: string
  location: string
  childPointer?: undefined
  children?: undefined
}

type StepWithChildren = {
  id: 3
  name: string
  title: string
  location: string
  childPointer: number
  children?: {
    index: number
    name: 'basicInfo' | 'mailingAddress' | 'businessAddress'
  }[]
}

export type ApplicationStep = StepWithoutChildren | StepWithChildren
type StepId = Pick<ApplicationStep, 'id'>['id']

type WizardContext = {
  goBack: () => void
  goNext: (from?: ApplicationStep) => void
  currentStep: ApplicationStep | null
  allStepData: { stepID: StepId; completed: boolean }[]
  setStepCompleted: (id: StepId) => void
}

const Context = createContext<WizardContext | undefined>(undefined)

export const ApplicationSteps: ApplicationStep[] = [
  {
    id: 0,
    name: 'Location Declaration',
    title: 'Location Declaration',
    location:
      AppRoutePaths.program_application.wizard.location_declaration.index,
  },
  {
    id: 1,
    name: 'Basic Info',
    title: 'Basic Info',
    location: AppRoutePaths.program_application.wizard.basic_info.index,
  },
  {
    id: 2,
    name: 'POC Info',
    title: 'POC Info',
    location: AppRoutePaths.program_application.wizard.poc_info.index,
  },
  {
    id: 3,
    name: 'Business Info',
    title: 'Business Info',
    location: AppRoutePaths.program_application.wizard.business_info.index,
    childPointer: 0,
    children: [
      { index: 0, name: 'basicInfo' },
      { index: 1, name: 'businessAddress' },
      { index: 2, name: 'mailingAddress' },
    ],
  },
  {
    id: 4,
    name: 'Service Info',
    title: 'Service Info',
    location: AppRoutePaths.program_application.wizard.service_info.index,
  },
  {
    id: 5,
    name: 'Consent and Review',
    title: 'Consent and Review',
    location: AppRoutePaths.program_application.wizard.consent_review.index,
  },
]

type StepData = Pick<WizardContext, 'allStepData'>['allStepData'][number]

export const WizardProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationIsChecklist = useMatch(
    AppRoutePaths.program_application.wizard.index
  )
  const [currentStep, setCurrentStep] = useState<ApplicationStep | null>(null)
  const initialStepData: StepData[] = ApplicationSteps.map((step) => ({
    stepID: step.id,
    completed: false,
  }))
  const [allStepData, updateAllStepData] = useState<StepData[]>(initialStepData)

  // Hook for the browser's native navigation buttons, this syncs the current step state
  useEffect(() => {
    const currentStepFromLocation = ApplicationSteps.find(
      (step) => step.location === location.pathname
    )
    if (currentStepFromLocation) {
      setCurrentStep(currentStepFromLocation)
      return
    }
    if (location.pathname === AppRoutePaths.program_application.wizard.index) {
      setCurrentStep(null)
      return
    }
  }, [location.pathname])

  const goNext = (from?: ApplicationStep) => {
    // Continue from last completed step case
    if (from && from.id + 1 < ApplicationSteps.length - 1) {
      navigate(ApplicationSteps[from.id + 1].location)
      setCurrentStep(ApplicationSteps[from.id + 1])
      return
    }

    // Initialization case
    if (currentStep == null) {
      navigate(ApplicationSteps[0].location)
      setCurrentStep(ApplicationSteps[0])
      return
    }

    // Handles case where user has click the Back To Checklist button
    if (locationIsChecklist != null) {
      navigate(ApplicationSteps[currentStep.id].location)
      return
    }

    // Last step case
    if (currentStep?.id == 5) {
      navigate(AppRoutePaths.program_application.wizard.index)
      setCurrentStep(null)
      return
    }

    // Step has sub-steps case
    if (
      currentStep.children &&
      currentStep.childPointer < currentStep.children.length - 1
    ) {
      setCurrentStep({
        ...currentStep,
        childPointer: currentStep?.childPointer + 1,
      })
      return
    }

    // Default case
    navigate(ApplicationSteps[currentStep.id + 1].location)
    setCurrentStep(ApplicationSteps[currentStep.id + 1])
  }

  const goBack = () => {
    if (currentStep == null) return
    if (currentStep.id == 0) {
      navigate(AppRoutePaths.program_application.wizard.index)
      setCurrentStep(null)
      return
    }
    if (currentStep.children && currentStep.childPointer > 0) {
      setCurrentStep({
        ...currentStep,
        childPointer: currentStep?.childPointer - 1,
      })
      return
    }

    navigate(ApplicationSteps[currentStep.id - 1].location)
    setCurrentStep(ApplicationSteps[currentStep.id - 1])
  }

  const setStepCompleted = (stepId: StepId) => {
    updateAllStepData((prevStepData) =>
      prevStepData.map((stepData) =>
        stepData.stepID === stepId ? { ...stepData, completed: true } : stepData
      )
    )
    return
  }

  return (
    <Context.Provider
      value={{
        goBack,
        goNext,
        currentStep,
        setStepCompleted,
        allStepData,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useWizardContext = () => {
  const context = useContext(Context)

  if (context === undefined)
    throw new Error('useWizardContext must be used within the WizardProvider')
  return context
}
